/**
 * Resolves the primary dashboard landing route based on user role or super admin status.
 *
 * Route destinations:
 * - Super Admin -> /admin/platform
 * - Admin / Executive / Director -> /admin/dashboard
 * - HR / People / Recruiter -> /hr/dashboard
 * - Finance / Accounts / CFO / Payroll -> /finance/dashboard
 * - IT / Tech / Systems / Developer -> /it/dashboard
 * - Employee / Self-Service / Others -> /employee/dashboard
 */
export function getDashboardRouteForRole(
  roleName?: string | null,
  isSuperAdmin?: boolean
): string {
  if (isSuperAdmin === true) {
    return "/admin/platform";
  }

  if (!roleName) {
    return "/employee/dashboard";
  }

  const role = roleName.trim().toLowerCase();

  if (role.includes("super") || role.includes("platform")) {
    return "/admin/platform";
  }

  // Employee portal check first if explicitly specified as employee/self-service/portal/member
  if (
    role.includes("self-service") ||
    role.includes("employee portal") ||
    role === "employee" ||
    role === "member" ||
    role === "user"
  ) {
    return "/employee/dashboard";
  }

  // Admin / Executive Leadership
  if (
    role.includes("admin") ||
    role.includes("ceo") ||
    role.includes("director") ||
    role.includes("founder") ||
    role.includes("chief executive")
  ) {
    return "/admin/dashboard";
  }

  // HR / People / Talent
  if (
    role === "hr" ||
    role.startsWith("hr ") ||
    role.endsWith(" hr") ||
    role.includes(" hr ") ||
    role.includes("human resource") ||
    role.includes("people") ||
    role.includes("recruiter") ||
    role.includes("talent")
  ) {
    return "/hr/dashboard";
  }

  // Finance / Accounts / Payroll
  if (
    role.includes("finance") ||
    role.includes("account") ||
    role.includes("payroll") ||
    role.includes("cfo") ||
    role.includes("treasury") ||
    role.includes("controller") ||
    role.includes("billing") ||
    role.includes("tax")
  ) {
    return "/finance/dashboard";
  }

  // Sales / CRM / BD
  if (
    role.includes("sales") ||
    role.includes("crm") ||
    role.includes("marketing") ||
    role.includes("business development") ||
    role.includes("bd")
  ) {
    return "/crm";
  }

  // IT / Tech / Systems
  if (
    role === "it" ||
    role.startsWith("it ") ||
    role.endsWith(" it") ||
    role.includes(" it ") ||
    role.includes("tech") ||
    role.includes("system") ||
    role.includes("developer") ||
    role.includes("engineer") ||
    role.includes("infra") ||
    role.includes("security")
  ) {
    return "/it/dashboard";
  }

  // Operations & Logistics
  if (
    role.includes("operation") ||
    role.includes("ops") ||
    role.includes("logistics") ||
    role.includes("field service")
  ) {
    return "/operations";
  }

  if (
    role.includes("procurement") ||
    role.includes("purchase") ||
    role.includes("buyer")
  ) {
    return "/procurement";
  }

  if (
    role.includes("inventory") ||
    role.includes("warehouse") ||
    role.includes("store")
  ) {
    return "/inventory";
  }

  if (
    role.includes("quality") ||
    role.includes("qa") ||
    role.includes("qc")
  ) {
    return "/quality";
  }

  if (
    role.includes("manufacturing") ||
    role.includes("production") ||
    role.includes("plant")
  ) {
    return "/manufacturing";
  }

  return "/employee/dashboard";
}
